import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import ChatRightSideBar from '../../Chat/ChatRightSideBar';
import SidebarCallHistory from '../SidebarCallHistory';
import MotivationPanel from '../MotivationPanel';
import { getSession } from '../../../auth';
import api from '../../../api';
import formatPhone from "../../../utils/format_phone";
import useNotification from "../../Notification";
import ClientDialog from "../../Dialogs/Client"
import dayjs from "dayjs";

export default function SidebarSwitcher({ setDialNumberFromHistory, ...props }) {
    const HISTORY_HARD_LIMIT = 100;
    const [showContact, setShowContact] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [contactData, setContactData] = React.useState(null);
    
    // Motivation Panel states
    const [showMotivationPanel, setShowMotivationPanel] = React.useState(false);
    const [selectedCallHistory, setSelectedCallHistory] = React.useState(null);
    const [callReasons, setCallReasons] = React.useState([]);
    const [selectedReasons, setSelectedReasons] = React.useState([]);
    const [lastCallId, setLastCallId] = React.useState(null);

    // Contact dialog states
    const [showCreateNewContactDialog, setShowCreateNewContactDialog] = React.useState(false);
    const [newContactPhone, setNewContactPhone] = React.useState("");

    const getHistoryStorageKey = () => {
        const u = getSession?.();
        const userId = u?.profile?.id || "anon";
        return `telephony:callHistory:${userId}`;
    };

    const patchHistoryWithContact = (callId, contact) => {
        if (!callId || !contact) return;

        try {
            const key = getHistoryStorageKey();
            const raw = localStorage.getItem(key);
            if (!raw) return;

            const list = JSON.parse(raw);
            if (!Array.isArray(list)) return;

            const idx = list.findIndex((item) => item.id === callId);
            if (idx === -1) return;

            list[idx] = {
                ...list[idx],
                contactName: contact.name || list[idx].contactName,
                contactEnterpriseName: contact.contact_enterprise_name || list[idx].contactEnterpriseName,
            };

            localStorage.setItem(
                key,
                JSON.stringify(list.slice(0, HISTORY_HARD_LIMIT))
            );

            // avisa o SidebarCallHistory pra recarregar
            window.dispatchEvent(new CustomEvent("callhistory:refresh"));
        } catch (e) {
            console.warn("Falha ao atualizar histórico com novo contato:", e);
        }
    };

    const loadContactFromHistory = React.useCallback(async (callItem) => {
        if (!callItem) return;

        try {
            setLoading(true);
            setContactData(null);

            const enterpriseId = getSession().profile.enterprise.id;
            const agentId = getSession().profile.agent_id;

            const rawCaller = callItem.phoneNumber || "";
            const hasUnderscore = rawCaller.includes("_");
            const digitsOnly = rawCaller.replace(/\D/g, "");

            // Caso 1: contato PBX (fila, ramal, agente) → iv_contacts
            if (hasUnderscore || digitsOnly.length < 8) {
                let searchValue = rawCaller;

                if (hasUnderscore) {
                    const parts = rawCaller.split("_");
                    searchValue = parts[1] ? parts[1] : parts[0];
                }

                const res = await api.get(
                    `/api/enterprises/${enterpriseId}/agents/${agentId}/iv_contacts/?limit=1&offset=0&contact_type=ALL&search=${encodeURIComponent(
                        searchValue
                    )}`,
                    { timeout: 15000 }
                );

                if (res.status >= 200 && res.status < 300) {
                    const data = res.data;
                    let contact = null;

                    if (Array.isArray(data.agent_contacts) && data.agent_contacts.length > 0) {
                        contact = data.agent_contacts[0];
                    } else if (Array.isArray(data.extension_contacts) && data.extension_contacts.length > 0) {
                        contact = data.extension_contacts[0];
                    } else if (Array.isArray(data.queue_contacts) && data.queue_contacts.length > 0) {
                        contact = data.queue_contacts[0];
                    }

                    if (contact) {
                        setShowContact(true);
                        setContactData(contact);
                        return;
                    }
                }
            }

            // Caso 2: contato de CRM → /contacts/?search
            const normalized = formatPhone(rawCaller);
            console.log("Searching CRM contact for number:", normalized);
            const res = await api.get(
                `api/enterprises/${enterpriseId}/contacts/?search=${encodeURIComponent(
                    normalized
                )}`,
                { timeout: 15000 }
            );

            if (res.status >= 200 && res.status < 300 && Array.isArray(res.data) && res.data.length > 0) {
                const contact = res.data[0];
                setShowContact(true);
                setContactData(contact);
                return;
            }
            // Nenhum contato encontrado → abrir diálogo de novo contato            
            setNewContactPhone(normalized || rawCaller);
            setShowCreateNewContactDialog(true);
        } catch (err) {
            console.error("Erro ao carregar contato a partir do histórico:", err);
            // Em caso de erro, também abrir diálogo de novo contato
            setNewContactPhone(callItem.phoneNumber || "");
            setShowCreateNewContactDialog(true);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load call reasons on mount
    React.useEffect(() => {
        const loadCallReasons = async () => {
            try {
                const enterpriseId = getSession().profile.enterprise.id;
                const agentId = getSession().profile.agent_id;
                const response = await api.get(`/api/enterprises/${enterpriseId}/agents/${agentId}/motivations/`);
                if (response.status >= 200 && response.status < 300) {
                    setCallReasons(response.data);
                }
            } catch (error) {
                console.error('Error loading call reasons:', error);
            }
        };
        loadCallReasons();
    }, []);

    // Handle call history item click
    const handleCallHistoryClick = React.useCallback((callItem) => {
        if (callItem && callItem.id) {
            setSelectedCallHistory(callItem);
            setSelectedReasons([]); // Reset selected reasons
            setShowMotivationPanel(true);
        }
    }, []);

    const handleHistoryItemClick = React.useCallback((callItem) => {
        // Clique na linha → abre ChatRightSideBar do contato (via request novo)
        if (callItem?.phoneNumber &&
            (callItem.phoneNumber.includes("_") || callItem.phoneNumber.length < 9)) {
            useNotification(
                "Informação",
                "Número interno não é elegível para cadastro no CRM.",
                "info");
            return;
        }
        setSelectedCallHistory(callItem);
        loadContactFromHistory(callItem);
    }, [loadContactFromHistory]);

    const createNewContactFromHistory = async (
        name,
        email,
        telephone,
        userFields,
        advisor,
        contactEnterprise,
        cpf,
        customer_code,
        groups,
        toCreateCustomFields,
        notificationOptOut,
        owner,
        birthday
    ) => {        
        if (!name && !email && !telephone) {
            useNotification(
                "Atenção!",
                "Insira pelo menos um: nome, email, ou telefone.",
                "warning"
            );
            return;
        }

        const enterpriseId = getSession().profile.enterprise.id;
        const formattedTelephone = telephone ? formatPhone(telephone) : null;

        const payload = {
            nick: formattedTelephone || email || name || undefined,
            name,
            email,
            telephone: formattedTelephone,
            user_fields: userFields,
            advisor,
            contact_enterprise: contactEnterprise,
            cpf,
            customer_code,
            notification_opt_out: notificationOptOut,
            groups,
            owner: owner === "" ? null : owner,
            birthday: birthday
                ? dayjs(birthday, "DD/MM/YYYY").format("YYYY-MM-DDTHH:mm")
                : null,
        };

        try {
            setLoading(true);
            const response = await api.post(
                `/api/enterprises/${enterpriseId}/contacts/`,
                payload
            );

            if (response.status !== 201) {
                // aqui você pode plugar o mesmo handleContactCreateErrors se quiser
                useNotification(
                    "Erro",
                    "Não foi possível criar o contato.",
                    "error"
                );
                return;
            }

            const createdContact = response.data;
            
            setShowCreateNewContactDialog(false);

            // atualiza o histórico só da ligação clicada
            if (selectedCallHistory?.id) {
                patchHistoryWithContact(selectedCallHistory.id, createdContact);
            }

            // e já abre a barra lateral com o contato recém-criado
            setContactData(createdContact);
            setShowContact(true);
        } catch (err) {
            console.error("Erro ao criar contato:", err);
            useNotification(
                "Erro",
                "Ocorreu um erro ao criar o contato.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };


    React.useEffect(() => {
        const onContact = (e) => {
            setLoading(true);
            const cdata = e.detail?.contact || null;
            console.log("Received contact data event:", cdata);
            if (cdata) {
                // You can process cdata here if needed
                setShowContact(true);
                setContactData(cdata);
            }
            setLoading(false);
        };
        const onHide = () => {
            setShowContact(false);
            setContactData(null);
            setLoading(false);
        };

        window.addEventListener('telephony:contact-data', onContact);
        window.addEventListener('telephony:hide-contact', onHide);
        return () => {
            window.removeEventListener('telephony:contact-data', onContact);
            window.removeEventListener('telephony:hide-contact', onHide);
        };
    }, []);

    if (!showContact) {
        // Sem ligação - histórico normal
        return (
            <>
                <SidebarCallHistory 
                    embedded 
                    height="100%" 
                    onItemClick={handleHistoryItemClick}
                    onMotivationClick={handleCallHistoryClick}
                    setDialNumberFromHistory={setDialNumberFromHistory}
                />
                {showMotivationPanel && (
                    <MotivationPanel
                        callReasons={callReasons}
                        open={showMotivationPanel}
                        onClose={() => {
                            setShowMotivationPanel(false);
                            setSelectedCallHistory(null);
                        }}
                        currentCallId={selectedCallHistory?.id}
                        saveReasons={async (reasons) => {
                            try {
                                const enterpriseId = getSession().profile.enterprise.id;
                                const agentId = getSession().profile.agent_id;
                                
                                if (!selectedCallHistory?.id) {
                                    console.error("No call history ID available to save motivations.");
                                    setShowMotivationPanel(false);
                                    return;
                                }

                                const payload = {
                                    uniqueid: selectedCallHistory.id,
                                    reasons: reasons.map(r => r.id),
                                    call_type: selectedCallHistory.direction || 'unknown',
                                };

                                const response = await api.post(
                                    `/api/enterprises/${enterpriseId}/agents/${agentId}/motivation/`,
                                    payload,
                                    { timeout: 15000 }
                                );

                                if (response.status >= 200 && response.status < 300) {
                                    console.log("Past call motivations saved successfully");
                                } else {
                                    console.error("Error saving past call motivations:", response);
                                }

                                setShowMotivationPanel(false);
                                setSelectedCallHistory(null);
                            } catch (error) {
                                console.error("Error saving past call motivations:", error);
                                setShowMotivationPanel(false);
                            }
                        }}
                        loadReasons={async () => {

                            try {
                                const enterpriseId = getSession().profile.enterprise.id;
                                const agentId = getSession().profile.agent_id;
                                
                                if (!selectedCallHistory?.id) {
                                    console.error("No call history ID available to save motivations.");
                                    setShowMotivationPanel(false);
                                    return;
                                }


                                const urlParameters = new URLSearchParams({ uniqueid: selectedCallHistory.id });

                                const response = await api.get(
                                    `/api/enterprises/${enterpriseId}/agents/${agentId}/motivation/`,
                                    { params: urlParameters, timeout: 15000 }
                                );

                                if (response.status >= 200 && response.status < 300) {
                                    console.log("Past call motivations retrieved successfully");
                                } else {
                                    console.error("Error retrieving past call motivations:", response);
                                }

                                return response.data || [];

                                // setShowMotivationPanel(false);
                                // setSelectedCallHistory(null);
                            } catch (error) {
                                console.error("Error saving past call motivations:", error);
                                // setShowMotivationPanel(false);
                            }
                            // Here you can implement loading previously saved reasons for a past call
                            // For now, we return an empty array
                            return [];
                        }}
                        selectedReasons={selectedReasons}
                        setSelectedReasons={setSelectedReasons}
                        setLastCallId={() => {}}
                        lastCallId={null}
                    />
                )}
                {showCreateNewContactDialog && (
                    <ClientDialog
                        open={true}
                        title="Novo cliente"
                        type="create"
                        model={{
                            telephone: newContactPhone,
                            // se quiser, pode pré-preencher outros campos aqui
                            // ex.:
                            // name: selectedCallHistory?.contactName || "",
                            // email: selectedCallHistory?.contactEmail || "",
                            // customer_code: selectedCallHistory?.clientCode || "",
                        }}
                        key={`ClientCreateFromHistory-${newContactPhone}`} // garante remount limpo
                        buttonText="Adicionar"
                        handleClose={() => setShowCreateNewContactDialog(false)}
                        submit={createNewContactFromHistory}
                        is_financial_market_company={false}
                        isAdmin={false}
                        listOfUsers={[getSession()]}
                        groups={[]}
                        customFields={[]}
                    />
                )}
            </>
        );
    }
    
    return (
        <Box sx={{ height: '100%' }}>
            {loading ? (
                <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <ChatRightSideBar
                    {...props}
                    profileContent="contact_data"
                    contactData={contactData}
                    closeChatRightSideBar={() => { setShowContact(false); setContactData(null); }}
                    parent={{ templates: [] }}
                    isManageCalls={false}
                />
            )}
        </Box>
    );
}
